/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * Moved out of ActionButtonsSection unchanged, so BOTH the column mapping and
 * the conditional-value dialog can render it.
 */

import React from 'react';
import Hint from './Hint';

export default /**
 * The four settings behind "A value from another table".
 *
 * Laid out as labelled rows rather than one inline sentence: this panel is the
 * narrow half of a split pane, and a sentence of five controls wrapped into an
 * unreadable ladder there. Each row is label + control, so it stays legible at
 * any width, and a plain-English summary underneath states what will actually
 * be written — the mapping is the part that needs explaining, not the widgets.
 */
function LookupFields({ column, value, tables, columnsOf, matchSources, onChange }) {
  // `id` is a real column the server will happily match on, but it is not one of
  // the table's declared fields — so without adding it here the commonest lookup
  // of all ("where id is this record's id") could not be configured at all.
  const declared = columnsOf(value.table);
  const cols = declared.some(c => c.name === 'id')
    ? declared
    : [{ name: 'id' }, ...declared];
  const needsValue = value.matchSource === 'const' || value.matchSource === 'record_field';
  const matchLabel = (matchSources.find(s => s.key === value.matchSource) || {}).label || '';

  const row = (label, control) => (
    <div className="d-flex align-items-center gap-2 mb-1">
      <span className="small text-muted text-end" style={{ minWidth: 74, flexShrink: 0 }}>
        {label}
      </span>
      {control}
    </div>
  );

  const complete = value.table && value.matchColumn && value.field;

  return (
    <div className="mt-1 mb-2 p-2 rounded border bg-body-tertiary">
      {row('Look in', (
        <select
          className="form-select form-select-sm"
          aria-label={`Lookup table for ${column}`}
          value={value.table}
          onChange={e => onChange({ table: e.target.value, matchColumn: '', field: '' })}
        >
          <option value="">— choose a table —</option>
          {tables.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
        </select>
      ))}

      {row('Match its', (
        <select
          className="form-select form-select-sm"
          aria-label={`Lookup match column for ${column}`}
          value={value.matchColumn}
          onChange={e => onChange({ matchColumn: e.target.value })}
          disabled={!value.table}
        >
          <option value="">— choose a column —</option>
          {cols.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
      ))}

      {row('against', (
        <select
          className="form-select form-select-sm"
          aria-label={`Lookup match value for ${column}`}
          value={value.matchSource}
          onChange={e => onChange({ matchSource: e.target.value, matchValue: '' })}
        >
          {matchSources.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      ))}

      {needsValue && row('', (
        <input
          className="form-control form-control-sm"
          aria-label={`Lookup match detail for ${column}`}
          placeholder={value.matchSource === 'record_field' ? 'which column' : 'which value'}
          value={value.matchValue}
          onChange={e => onChange({ matchValue: e.target.value })}
        />
      ))}

      {row('Then write', (
        <select
          className="form-select form-select-sm"
          aria-label={`Lookup field for ${column}`}
          value={value.field}
          onChange={e => onChange({ field: e.target.value })}
          disabled={!value.table}
        >
          <option value="">— choose a column —</option>
          {cols.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
      ))}

      <Hint>
        {complete
          ? `Finds the ${value.table} row whose ${value.matchColumn} equals `
            + `${matchLabel}${needsValue && value.matchValue ? ` (${value.matchValue})` : ''}, `
            + `and saves that row's ${value.field} into ${column}. `
            + 'Read on the server — the browser never sends it.'
          : 'Pick a table, the column to match on, and the column to read.'}
      </Hint>
    </div>
  );
}
